import {
  Avatar,
  Button,
  DebouncedTextField,
  DeleteConfirmButton,
  Divider,
  Drawer,
  FileDrop,
  Flex,
  IconButton,
  Menu,
  Text,
  toaster,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { getDownloadURL } from 'firebase/storage'
import { startCase } from 'lodash-es'
import { IUser, PermissionValue, User } from 'types'
import { acceptedImages, uploadFile, useEntevatePermission, usePermission } from 'utils'
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  LockClosedIcon,
  PencilSquareIcon,
  ShareIcon,
  TrashIcon,
} from '../icons'
import PermissionWithHelp, { permissionOptions } from './PermissionWithHelp'

interface Props {
  copyInviteLink: () => void
  member?: User
  onDone?: () => void
  onMemberUpdated: (body: Partial<User>) => void
  onRemoveMember: () => void
  openDrawer?: boolean
  updatePermissions: (permission: PermissionValue) => void
}

export function WorkspaceUserDrawer({
  copyInviteLink,
  member,
  onDone,
  onMemberUpdated,
  onRemoveMember,
  openDrawer,
  updatePermissions,
}: Props) {
  const currentUser = useCurrentUser()
  const [entevatePermission] = useEntevatePermission()
  const permission = usePermission()
  const isWorkspaceAdmin = permission.workspace
  const isEntevateUser = currentUser?.isAdmin && entevatePermission === 'entevate'
  const canEditMember = isEntevateUser && member?.id && member?.status !== 'pending'

  async function handleUpdateField(field: Partial<IUser>) {
    onMemberUpdated(field)
  }

  async function handleImageFile(files: File[]) {
    try {
      if (!files?.length || !member?.id) throw new Error('File type not accepted')

      const imageFile = files[0]
      const ref = await uploadFile(`users/profile-pictures/${member.id}`, imageFile)
      const downloadURL = await getDownloadURL(ref)
      await handleUpdateField({ photoUrl: downloadURL })
      toaster.create({ title: 'Profile picture updated', type: 'success' })
    } catch (error: any) {
      console.error(error)
      toaster.create({ title: error.message, type: 'error' })
    }
  }

  async function handleRemovePhoto() {
    await handleUpdateField({ photoUrl: '' })
    toaster.create({ title: 'Profile picture removed', type: 'success' })
  }

  return (
    <Drawer
      open={openDrawer!}
      key={member?.permission}
      onClose={() => onDone?.()}
      header="Manage Member"
      headerIcon={<Cog6ToothIcon />}
    >
      {member && (
        <Flex direction={'column'} gap={6} mt={3}>
          {canEditMember ? (
            <FileDrop
              overlayProps={{ borderRadius: 'md' }}
              onDrop={handleImageFile}
              options={{ accept: acceptedImages, maxSize: 2 * 1024 * 1024 }}
            >
              {({ open }) => (
                <Flex gap={2} align="center">
                  <Avatar
                    key={member.photoUrl}
                    name={member.name}
                    src={member.photoUrl}
                    size="md"
                  />
                  <Flex direction="column" gap={1}>
                    <IconButton
                      aria-label="edit-profile-picture"
                      variant="ghost"
                      size="xs"
                      icon={<PencilSquareIcon fontSize="sm" />}
                      onClick={open}
                    />
                    {member.photoUrl && (
                      <IconButton
                        aria-label="remove-profile-picture"
                        variant="ghost"
                        size="xs"
                        icon={<TrashIcon fontSize="sm" />}
                        onClick={handleRemovePhoto}
                      />
                    )}
                  </Flex>
                </Flex>
              )}
            </FileDrop>
          ) : (
            <Flex gap={2} align="center">
              <Avatar name={member.name} src={member.photoUrl} size="md" />
              <Flex direction="column" justify="center">
                {member.name && <Text textStyle="bodySmall">{member.name}</Text>}
                <Text
                  textStyle="bodySmall"
                  fontWeight="500"
                  color={member.name || member.email === 'Pending' ? 'gray.500' : 'black'}
                >
                  {member.email}
                </Text>
              </Flex>
            </Flex>
          )}

          {canEditMember && (
            <Flex direction="column" gap={3}>
              <EditableField
                label="Full Name"
                onChange={name => handleUpdateField({ name })}
                placeholder="Enter full name..."
                value={member.name || ''}
              />
              <Divider borderColor="rgba(23, 23, 23, 0.10)" />
              <Flex gap={3} direction={{ base: 'column', sm: 'row' }}>
                <EditableField
                  label="Company"
                  onChange={company => handleUpdateField({ company })}
                  placeholder="Enter company..."
                  value={member.company || ''}
                />
                <EditableField
                  label="Title"
                  onChange={title => handleUpdateField({ title })}
                  placeholder="Enter title..."
                  value={member.title || ''}
                />
              </Flex>
              <Divider borderColor="rgba(23, 23, 23, 0.10)" />
              <Text textStyle="bodySmall" fontWeight="500" color="gray.500">
                {member.email}
              </Text>
            </Flex>
          )}

          {member.status === 'pending' && (
            <Button
              alignSelf="flex-start"
              color="secondary.700"
              variant="link"
              leftIcon={<ShareIcon fontSize="sm" />}
              onClick={copyInviteLink}
            >
              Copy invite link
            </Button>
          )}

          <Flex gap={2} align={'center'}>
            <LockClosedIcon type="outline" />
            <Text textStyle={'bodyMedium'}>Permissions</Text>
          </Flex>
          <Menu
            menuProps={{ positioning: { sameWidth: true } }}
            disabled={!(isWorkspaceAdmin || isEntevateUser)}
            rightIcon={<ChevronDownIcon fontSize={'md'} />}
            gap={{ base: 0, sm: 2 }}
            borderRadius="sm"
            variant="outline"
            textAlign={'start'}
            buttonLabel={`Role: ${startCase(member.permission)}`}
            value={member.permission}
            onChange={value => {
              updatePermissions(value as PermissionValue)
              onDone?.()
            }}
            options={permissionOptions.map(({ value }) => ({
              value,
              label: <PermissionWithHelp value={value} />,
            }))}
          />

          <Flex
            align={'flex-start'}
            backdropFilter="blur(27px)"
            border="1px solid rgba(23, 23, 23, 0.20)"
            borderRadius="10px"
            direction="column"
            gap={3}
            p={5}
          >
            <Flex direction="column" gap={1}>
              <Text textStyle="bodyMedium">Delete Member</Text>
              <Text textStyle="bodySmall" fontWeight="normal">
                This action cannot be undone. All associated member data will be permanently
                deleted.
              </Text>
            </Flex>
            <DeleteConfirmButton
              p={5}
              m={0}
              variant="solid"
              onDelete={() => {
                onRemoveMember()
                onDone?.()
              }}
              description={
                'This action cannot be undone. All associated member data will be permanently deleted.'
              }
            />
          </Flex>
        </Flex>
      )}
    </Drawer>
  )
}

interface EditableFieldProps {
  label: string
  onChange: (value: string) => void
  placeholder: string
  value: string
}

function EditableField({ label, onChange, placeholder, value }: EditableFieldProps) {
  return (
    <DebouncedTextField
      bg="#fff"
      backdropFilter="blur(27px)"
      border="1px solid rgba(23, 23, 23, 0.20)"
      borderRadius="10px"
      label={label}
      onChange={onChange}
      placeholder={placeholder}
      showStatus
      value={value}
    />
  )
}
